import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Login from "./pages/Login";
import Shell from "./components/layout/Shell";
import ProtectedRoute from "./components/ProtectedRoute";
import { ConfirmDialogProvider } from "./components/common/ConfirmDialog";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { NewVersionBanner } from "./components/common/NewVersionBanner";
import { useNativeFcm } from "./hooks/useNativeFcm";

/**
 * WarnixAI v2 — Router.
 *
 * Login is eager (it's the first paint). Every authenticated page is
 * code-split with React.lazy so the initial bundle stays small — a field
 * phone on the Capacitor APK downloads only what it navigates to. Each page
 * is wrapped in its own <ErrorBoundary> so a crash in one page never nukes
 * the whole app; a single <Suspense> handles the lazy-chunk loading state.
 */
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const LiveMonitor = lazy(() => import("./pages/LiveMonitor"));
const Cameras = lazy(() => import("./pages/Cameras"));
const Zones = lazy(() => import("./pages/Zones"));
const Alerts = lazy(() => import("./pages/Alerts"));
const AlertDetail = lazy(() => import("./pages/AlertDetail"));
const AiHub = lazy(() => import("./pages/AiHub"));
const CtoFeed = lazy(() => import("./pages/CtoFeed"));
const Workers = lazy(() => import("./pages/Workers"));
const Vehicles = lazy(() => import("./pages/Vehicles"));
const Pinkas = lazy(() => import("./pages/Pinkas"));
const Compliance = lazy(() => import("./pages/Compliance"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const Help = lazy(() => import("./pages/Help"));

function RouteFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center py-24 text-slate-400 text-sm">
      {t("common.loading")}
    </div>
  );
}

export default function App() {
  useNativeFcm();
  const { t } = useTranslation();
  /** Wrap a page in its own error boundary, labelled with its translated name
   *  so the fallback can say WHICH page failed. */
  const wrap = (navKey: string, el: JSX.Element) => (
    <ErrorBoundary label={t(navKey)}>{el}</ErrorBoundary>
  );
  return (
    <ConfirmDialogProvider>
      <NewVersionBanner />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={wrap("nav.login", <Login />)} />

          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                {wrap("nav.onboarding", <Onboarding />)}
              </ProtectedRoute>
            }
          />

          <Route
            element={
              <ProtectedRoute>
                <Shell />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={wrap("nav.dashboard", <Dashboard />)} />
            <Route path="/monitor" element={wrap("nav.monitor", <LiveMonitor />)} />
            <Route path="/cameras" element={wrap("nav.cameras", <Cameras />)} />
            <Route path="/zones" element={wrap("nav.zones", <Zones />)} />
            <Route path="/alerts" element={wrap("nav.alerts", <Alerts />)} />
            <Route path="/alerts/:id" element={wrap("nav.alerts", <AlertDetail />)} />
            <Route path="/ai-inspector" element={wrap("nav.aiInspector", <CtoFeed />)} />
            <Route path="/workers" element={wrap("nav.workers", <Workers />)} />
            <Route path="/vehicles" element={wrap("nav.vehicles", <Vehicles />)} />
            <Route path="/pinkas" element={wrap("nav.pinkas", <Pinkas />)} />
            <Route path="/compliance" element={wrap("nav.compliance", <Compliance />)} />
            <Route path="/reports" element={wrap("nav.reports", <Reports />)} />
            <Route path="/settings" element={wrap("nav.settings", <Settings />)} />
            <Route path="/help" element={wrap("nav.help", <Help />)} />

            {/* AI hub (admin-only) */}
            <Route
              path="/ai"
              element={
                <ProtectedRoute requireRole={["admin"]}>
                  {wrap("nav.aiHub", <AiHub />)}
                </ProtectedRoute>
              }
            />

            {/* Legacy routes — single entry point is the AI hub / Reports tabs.
                Old bookmarks and APK deep links keep working via redirects. */}
            <Route path="/ai-assistant" element={<Navigate to="/ai?tab=assistant" replace />} />
            <Route path="/alert-config" element={<Navigate to="/ai?tab=alerts" replace />} />
            <Route path="/model-settings" element={<Navigate to="/ai?tab=models" replace />} />
            <Route path="/efficiency" element={<Navigate to="/reports?tab=efficiency" replace />} />
          </Route>

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </ConfirmDialogProvider>
  );
}
